import { type ItemDeChecklist, salvarChecklistSchema } from '@locaveri/shared'
import { ArrowDown, ArrowUp, ListChecks, LoaderCircle, Plus, Trash2 } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useChecklist, useSalvarChecklist } from '@/features/vistorias/api'
import { errorMessage } from '@/lib/api'
import { usePermissao } from '@/lib/permissoes'

/**
 * O checklist de vistoria do tenant — os itens que o pátio confere em toda
 * saída e todo retorno (estepe, triângulo, documento…).
 *
 * ---------------------------------------------------------------------------
 * O CONJUNTO É SALVO INTEIRO, COMO NAS TARIFAS
 * ---------------------------------------------------------------------------
 * `PUT /vistorias/checklist` substitui a configuração de uma vez: a unicidade
 * das chaves e a ordem são propriedades do CONJUNTO, não de uma linha. A tela
 * edita uma cópia local (adicionar, remover, reordenar por setas) e só o
 * salvar grava — um refetch em segundo plano nunca apaga o que a pessoa está
 * ajustando.
 *
 * A ORDEM é a posição na lista: `ordemDeExibicao` sai do índice no salvar, e
 * as setas são o gesto de reordenar (drag seria mais código para o mesmo
 * resultado numa lista de no máximo 60 itens).
 *
 * ---------------------------------------------------------------------------
 * OBRIGATÓRIO VALE JÁ — E A TELA AVISA ANTES
 * ---------------------------------------------------------------------------
 * Item obrigatório passa a ser exigido em TODA vistoria registrada a partir do
 * salvar (as já gravadas não mudam: vistoria é imutável). O aviso fica fixo no
 * topo porque a consequência é imediata e acontece longe daqui — no pátio, com
 * o cliente esperando.
 *
 * A permissão de escrita é a MESMA que a API exige no `PUT`
 * (`inspection.update`); sem ela a tela é somente leitura, porque oferecer o
 * salvar para quem vai levar 403 é prometer o que não se cumpre. Os ÂNGULOS de
 * foto obrigatórios não aparecem aqui de propósito: são lista fechada no
 * código, não configuração — a foto que falta hoje é a prova que faltará na
 * disputa.
 */

interface ItemEditado {
  /** Chave de renderização estável enquanto a pessoa edita a `chave` real. */
  readonly chaveLocal: string
  readonly chave: string
  readonly rotulo: string
  readonly obrigatorio: boolean
}

function paraEdicao(itens: readonly ItemDeChecklist[]): readonly ItemEditado[] {
  return [...itens]
    .sort((umItem, outroItem) =>
      umItem.ordemDeExibicao === outroItem.ordemDeExibicao
        ? umItem.rotulo.localeCompare(outroItem.rotulo)
        : umItem.ordemDeExibicao - outroItem.ordemDeExibicao,
    )
    .map((item) => ({
      chaveLocal: crypto.randomUUID(),
      chave: item.chave,
      rotulo: item.rotulo,
      obrigatorio: item.obrigatorio,
    }))
}

export function ChecklistPage(): ReactElement {
  const podeEditar = usePermissao('inspection.update')

  const consulta = useChecklist()
  const salvar = useSalvarChecklist()

  const [itens, setItens] = useState<readonly ItemEditado[] | null>(null)
  const [errosPorItem, setErrosPorItem] = useState<Readonly<Record<number, string>>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  // Preenche o formulário quando a leitura chega — uma vez. Depois disso o
  // estado é da pessoa (mesmo padrão dos pesos de alocação).
  useEffect(() => {
    if (itens === null && consulta.data !== undefined) {
      setItens(paraEdicao(consulta.data.itens))
    }
  }, [itens, consulta.data])

  const ocupado = salvar.isPending

  function editar(indice: number, mudanca: Partial<Omit<ItemEditado, 'chaveLocal'>>): void {
    setItens((atuais) =>
      atuais === null
        ? atuais
        : atuais.map((item, posicao) => (posicao === indice ? { ...item, ...mudanca } : item)),
    )
  }

  function adicionar(): void {
    setItens((atuais) => [
      ...(atuais ?? []),
      { chaveLocal: crypto.randomUUID(), chave: '', rotulo: '', obrigatorio: true },
    ])
  }

  function remover(indice: number): void {
    setItens((atuais) =>
      atuais === null ? atuais : atuais.filter((_, posicao) => posicao !== indice),
    )
    setErrosPorItem({})
  }

  function mover(indice: number, delta: -1 | 1): void {
    setItens((atuais) => {
      if (atuais === null) {
        return atuais
      }

      const destino = indice + delta

      if (destino < 0 || destino >= atuais.length) {
        return atuais
      }

      const copia = [...atuais]
      const [movido] = copia.splice(indice, 1)

      if (movido === undefined) {
        return atuais
      }

      copia.splice(destino, 0, movido)
      return copia
    })
    setErrosPorItem({})
  }

  function aoSalvar(): void {
    if (itens === null) {
      return
    }

    setErrosPorItem({})
    setErroGeral(null)

    // A ordem É a posição na lista — `ordemDeExibicao` nasce do índice.
    const corpo = {
      itens: itens.map((item, indice) => ({
        chave: item.chave.trim(),
        rotulo: item.rotulo,
        obrigatorio: item.obrigatorio,
        ordemDeExibicao: indice,
      })),
    }

    const validado = salvarChecklistSchema.safeParse(corpo)

    if (!validado.success) {
      const porItem: Record<number, string> = {}
      const sobras: string[] = []

      for (const issue of validado.error.issues) {
        const indice = issue.path[1]

        if (issue.path[0] === 'itens' && typeof indice === 'number') {
          const atual = porItem[indice]
          porItem[indice] = atual === undefined ? issue.message : `${atual} · ${issue.message}`
        } else {
          // O refinamento de chave duplicada aponta para o conjunto.
          sobras.push(issue.message)
        }
      }

      setErrosPorItem(porItem)
      setErroGeral(sobras.length > 0 ? sobras.join(' · ') : null)
      return
    }

    salvar.mutate(validado.data, {
      onSuccess: (resposta) => {
        toast.success('Checklist salvo', {
          description:
            'Os itens obrigatórios já valem para toda vistoria registrada daqui em diante.',
        })
        setItens(paraEdicao(resposta.itens))
      },
      onError: (causa: unknown) => {
        const mensagem = errorMessage(causa)

        setErroGeral(mensagem)
        toast.error('Não foi possível salvar o checklist', { description: mensagem })
      },
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Checklist de vistoria</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          Os itens que o pátio confere em toda vistoria de saída e de retorno. Os ângulos de foto
          obrigatórios não são configuráveis — são lista fechada no código, porque a foto que falta
          hoje é a prova que faltará na disputa.
        </p>
      </div>

      {podeEditar ? null : (
        <Alert variant="neutral">
          <AlertDescription>
            Somente leitura: editar o checklist exige a permissão de vistoria (inspection.update).
          </AlertDescription>
        </Alert>
      )}

      <Alert variant="warning">
        <AlertTitle>Item obrigatório passa a valer imediatamente</AlertTitle>
        <AlertDescription>
          A partir do salvar, TODA vistoria nova exige resposta nos itens marcados como obrigatórios
          — a de saída que estiver sendo preenchida no pátio inclusive. As vistorias já registradas
          não mudam: vistoria é imutável.
        </AlertDescription>
      </Alert>

      {consulta.isPending || itens === null ? (
        consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar o checklist"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : (
          <LoadingState linhas={4} rotulo="Carregando o checklist…" />
        )
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Itens ({String(itens.length)})</CardTitle>
            <CardDescription>
              A ordem da lista é a ordem em que o pátio confere — use as setas para reordenar. A
              chave é o identificador estável do item: é ela que casa a configuração com as
              respostas já gravadas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {erroGeral === null ? null : (
              <Alert variant="danger">
                <AlertDescription>{erroGeral}</AlertDescription>
              </Alert>
            )}

            {itens.length === 0 ? (
              <EmptyState
                icone={ListChecks}
                titulo="Nenhum item configurado"
                descricao="Sem checklist a vistoria fecha só com fotos, avarias e assinatura. Adicione o que a sua operação confere — estepe, triângulo, documento do carro…"
                acao={
                  podeEditar ? (
                    <Button type="button" onClick={adicionar}>
                      <Plus aria-hidden="true" />
                      Adicionar item
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <ol className="space-y-3">
                {itens.map((item, indice) => {
                  const erro = errosPorItem[indice]

                  return (
                    <li
                      key={item.chaveLocal}
                      className="space-y-3 rounded-lg border border-border bg-surface p-3"
                    >
                      <div className="flex flex-wrap items-start gap-3">
                        <span className="mt-2 w-6 shrink-0 text-sm font-semibold tabular-money text-fg-secondary">
                          {String(indice + 1)}.
                        </span>

                        <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
                          <Campo rotulo="Rótulo" obrigatorio>
                            {(campo) => (
                              <Input
                                {...campo}
                                value={item.rotulo}
                                placeholder="Ex.: Estepe e macaco"
                                disabled={!podeEditar || ocupado}
                                onChange={(evento) => {
                                  editar(indice, { rotulo: evento.target.value })
                                }}
                              />
                            )}
                          </Campo>

                          <Campo
                            rotulo="Chave"
                            obrigatorio
                            dica="Minúsculas, números e hífen. Não mude a chave de um item antigo — as respostas já gravadas apontam para ela."
                          >
                            {(campo) => (
                              <Input
                                {...campo}
                                value={item.chave}
                                placeholder="ex.: estepe"
                                disabled={!podeEditar || ocupado}
                                onChange={(evento) => {
                                  editar(indice, { chave: evento.target.value })
                                }}
                              />
                            )}
                          </Campo>
                        </div>

                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-label={`Mover o item ${String(indice + 1)} para cima`}
                              onClick={() => {
                                mover(indice, -1)
                              }}
                              disabled={!podeEditar || ocupado || indice === 0}
                            >
                              <ArrowUp aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-label={`Mover o item ${String(indice + 1)} para baixo`}
                              onClick={() => {
                                mover(indice, 1)
                              }}
                              disabled={!podeEditar || ocupado || indice === itens.length - 1}
                            >
                              <ArrowDown aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-label={`Remover o item ${String(indice + 1)}`}
                              onClick={() => {
                                remover(indice)
                              }}
                              disabled={!podeEditar || ocupado}
                            >
                              <Trash2 aria-hidden="true" />
                            </Button>
                          </div>

                          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                            <Checkbox
                              checked={item.obrigatorio}
                              disabled={!podeEditar || ocupado}
                              onChange={(evento) => {
                                editar(indice, { obrigatorio: evento.target.checked })
                              }}
                            />
                            Obrigatório
                            {item.obrigatorio ? null : <Badge variant="neutral">Opcional</Badge>}
                          </label>
                        </div>
                      </div>

                      {erro === undefined ? null : <p className="text-xs text-danger-fg">{erro}</p>}
                    </li>
                  )
                })}
              </ol>
            )}

            {podeEditar ? (
              <div className="flex flex-wrap items-center gap-3">
                {itens.length === 0 ? null : (
                  <Button type="button" variant="secondary" onClick={adicionar} disabled={ocupado}>
                    <Plus aria-hidden="true" />
                    Adicionar item
                  </Button>
                )}
                <Button type="button" onClick={aoSalvar} disabled={ocupado}>
                  {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                  Salvar checklist
                </Button>
                <p className="text-xs text-fg-muted">
                  O conjunto é salvo inteiro — remover um item daqui o tira das PRÓXIMAS vistorias,
                  nunca das já registradas.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
